import { Theme } from '@eversports/react-components/design-tokens/theme'
import Button from '@eversports/react-components/primitives/Button'
import Flex, { SimpleFlex, VerticalFlex } from '@eversports/react-components/primitives/Flex'
import Icon, { Icons } from '@eversports/react-components/primitives/Icon'
import Text from '@eversports/react-components/primitives/Text'
import { makeStyles } from '@mui/styles'
import { usePyroField } from 'pyro-form'
import React, { useEffect, useState } from 'react'

import useCheckoutContext from '../../data/useCheckoutContext'
import { Localized } from '../../localization/react'
import { Mandate, StoredBankAccountWithMandates } from '../../typings/Payment'

import NewBankAccount from './NewBankAccount'
import StoredBankAccount from './StoredBankAccount'

const useStyles = makeStyles((theme: Theme) => ({
  newSepaAccount: {
    border: theme.borders.default,
    borderRadius: theme.borderRadii.default,
    alignSelf: 'flex-start',
  },
}))

interface Props {
  venueMandate: Mandate | null
  storedBankAccounts: Array<StoredBankAccountWithMandates>
}

const OnlineSepaForm: React.FC<React.PropsWithChildren<Props>> = ({ venueMandate, storedBankAccounts }) => {
  const context = useCheckoutContext()
  const classes = useStyles()

  const [showAddNewSepaAccount, setShowAddNewSepaAccount] = useState(false)
  const { core: bankAccountIdPyroField } = usePyroField('pspPaymentMethodId')

  const handleShowAddNewSepaAccountClick = () => {
    setShowAddNewSepaAccount(true)
  }

  useEffect(() => {
    if (venueMandate && venueMandate.bankAccount && venueMandate.isVerified) {
      bankAccountIdPyroField.onChange(venueMandate.bankAccount.pspId)
      context.setIsPaymentValid(true)
    } else {
      context.setIsPaymentValid(false)
    }
  }, [])

  useEffect(() => {
    if (!bankAccountIdPyroField.value) return

    setShowAddNewSepaAccount(false)
    context.setIsPaymentValid(true)
  }, [bankAccountIdPyroField.value])

  return (
    <VerticalFlex verticalSpacing="default">
      <Text fontWeight="bold">
        <Localized id="pay-with" params={{ paymentMethodType: 'SEPA' }} />
      </Text>
      <SimpleFlex alignItems="flex-start">
        <Icon icon={Icons.INFO} size="large" color="subtleText" />
        <Text marginLeft="dense" size="small">
          <Localized id="sepa-payment-information" />
        </Text>
      </SimpleFlex>
      {(venueMandate || storedBankAccounts.length > 0) && (
        <Text color="subtleText">
          <Localized id="sepa-saved-bank-account-header" />
        </Text>
      )}
      {venueMandate ? (
        <StoredBankAccount bankAccount={venueMandate.bankAccount} isVerified={venueMandate.isVerified} isSelected />
      ) : (
        <Flex flexDirection="column" verticalSpacing="default">
          {storedBankAccounts.map((bankAccount) => (
            <StoredBankAccount
              key={bankAccount.pspId}
              bankAccount={bankAccount}
              isSelected={bankAccount.pspId === bankAccountIdPyroField.value}
            />
          ))}
          {showAddNewSepaAccount ? (
            <NewBankAccount />
          ) : (
            <Button variant="secondary" onClick={handleShowAddNewSepaAccountClick} className={classes.newSepaAccount}>
              <Text size="small" fontWeight="bold" color="primary.main">
                <Localized id="sepa-new-bank-account" />
              </Text>
            </Button>
          )}
        </Flex>
      )}
    </VerticalFlex>
  )
}

export default React.memo(OnlineSepaForm)
